import React, {
  useState,
  useEffect,
  useRef,
  useImperativeHandle,
  forwardRef
} from "react";
import PropTypes from "kf-proptypes/widgets";
import dayjs from "dayjs";
import customParseFormat from "dayjs/plugin/customParseFormat";
import advancedFormat from "dayjs/plugin/advancedFormat";
import { FieldLabel, FieldHint, FieldErrors } from "@widgets/inputs/fields";

import {
  formatToTimeZone,
  getFormattedDate,
  dateTimeFormatString,
  formatToLocale
} from "base/utils";
import { useUserStore } from "base/stores";

import { DatePicker } from "./datetimepicker.jsx";
import {
  PickerInput,
  PICKERINPUT_SIZE,
  PICKERINPUT_TYPE
} from "./pickerinput.jsx";

export const DATEPICKER_SIZE = PICKERINPUT_SIZE;

export const DATEPICKER_TYPE = PICKERINPUT_TYPE;

dayjs.extend(customParseFormat);
dayjs.extend(advancedFormat);

export function DatePickerInputComponent(props, ref) {
  let {
    dateString,
    onChange,
    onOpen,
    onClose,
    onFocus,
    includeTime = false,
    isOpen,
    readOnly,
    disabled,
    autoFocus,
    startsAt,
    endsAt,
    type = DATEPICKER_TYPE.NORMAL,
    size = DATEPICKER_SIZE.BASE,
    isError,
    hideNow = false,
    className,
    showCloseIcon = true,
    customTimeZone,
    id,
    label,
    required,
    hint,
    errors = [],
    headerRenderer,
    footerRenderer,
    isHoverMode = false,
    showTimeZoneDescription = false,
    labelLeftIcon,
    labelRightIcon
  } = props;

  const { currentUser } = useUserStore.getState();

  const [momentDate, updateMomentDate] = useState(null);
  const [formattedDate, updateDateInLocale] = useState(null);
  const pickerInputRef = useRef(null);

  useEffect(
    function dateStringEffect() {
      if (dateString) {
        let convertTime;
        let dateTimeFormat;
        if (includeTime) {
          let locale =
            (currentUser && currentUser.DateFormat) ||
            Intl.DateTimeFormat().resolvedOptions().locale;
          let userTimeZone =
            (currentUser && currentUser.TimeZone._id) ||
            Intl.DateTimeFormat().resolvedOptions().timeZone;
          dateTimeFormat = dateTimeFormatString(locale);
          convertTime = formatToTimeZone(
            dateString.split(" ")[0],
            locale,
            userTimeZone
          );
        }
        const convertToUTCFormat = includeTime
          ? getFormattedDate(
              convertTime,
              "YYYY-MM-DDTHH:mm:00Z",
              dateTimeFormat
            )
          : dateString;
        let value = convertToUTCFormat && convertToUTCFormat.split(/[^0-9]/);
        let momentDate;
        if (includeTime) {
          momentDate = dayjs([
            value[0],
            value[1] - 1,
            value[2],
            value[3],
            value[4]
          ]);
        } else {
          momentDate = dayjs([value[0], value[1] - 1, value[2]]);
        }

        let formattedDate = formatToLocale(dateString, includeTime, true);

        updateDateInLocale(formattedDate);
        updateMomentDate(momentDate);
      } else {
        updateMomentDate(dayjs());
        updateDateInLocale(dateString);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [dateString]
  );

  useImperativeHandle(ref, () => {
    return {
      focus: () => {
        pickerInputRef.current.focus();
      },
      blur: () => {
        pickerInputRef.current.blur();
      }
    };
  });

  const updateInputValue = function updateInputValue(dateString) {
    if (dateString) {
      let formattedDate = formatToLocale(dateString, includeTime);
      updateDateInLocale(formattedDate);
    } else {
      updateDateInLocale(null);
    }
  };

  return (
    <React.Fragment>
      {label && (
        <FieldLabel
          label={label}
          required={required}
          targetId={id}
          disabled={disabled}
          leftIcon={labelLeftIcon}
          rightIcon={labelRightIcon}
          size={size}
        />
      )}
      {readOnly ? (
        <PickerInput
          value={formattedDate}
          dateString={dateString}
          includeTime={includeTime}
          readOnly={readOnly}
          onFocus={onFocus}
          autoFocus={autoFocus}
          ref={pickerInputRef}
          type={type}
          size={size}
          isHoverMode={isHoverMode}
          disabled={disabled}
          className={className}
          showTimeZoneDescription={showTimeZoneDescription}
        />
      ) : (
        <DatePicker
          momentDate={momentDate}
          includeTime={includeTime}
          startsAt={startsAt}
          endsAt={endsAt}
          onOpen={onOpen}
          onClose={onClose}
          isOpen={isOpen}
          target={
            <PickerInput
              value={formattedDate}
              dateString={dateString}
              includeTime={includeTime}
              onChange={onChange}
              onFocus={onFocus}
              autoFocus={autoFocus}
              ref={pickerInputRef}
              type={type}
              size={size}
              isHoverMode={isHoverMode}
              disabled={disabled}
              isError={isError || errors?.length > 0}
              showCloseIcon={showCloseIcon}
              customTimeZone={customTimeZone}
              showTimeZoneDescription={showTimeZoneDescription}
            />
          }
          onChange={onChange}
          updateInputValue={updateInputValue}
          dateString={dateString}
          hideNow={hideNow}
          headerRenderer={headerRenderer}
          footerRenderer={footerRenderer}
        />
      )}
      {hint && <FieldHint hint={hint} targetId={id} />}
      {errors && errors.length > 0 && (
        <FieldErrors errors={errors} targetId={id} />
      )}
    </React.Fragment>
  );
}

DatePickerInputComponent.propTypes = {
  dateString: PropTypes.string,
  includeTime: PropTypes.bool,
  onChange: PropTypes.func,
  onOpen: PropTypes.func,
  onClose: PropTypes.func,
  onFocus: PropTypes.func,
  readOnly: PropTypes.bool,
  isOpen: PropTypes.bool,
  autoFocus: PropTypes.bool,
  startsAt: PropTypes.oneOfType([
    PropTypes.number,
    PropTypes.string,
    PropTypes.bool,
    PropTypes.object
  ]),
  endsAt: PropTypes.oneOfType([
    PropTypes.number,
    PropTypes.string,
    PropTypes.bool,
    PropTypes.object
  ]),
  type: PropTypes.string,
  size: PropTypes.string,
  disabled: PropTypes.bool,
  isError: PropTypes.bool,
  className: PropTypes.string,
  showCloseIcon: PropTypes.bool,
  hideNow: PropTypes.bool,
  customTimeZone: PropTypes.string,
  id: PropTypes.string,
  label: PropTypes.string,
  required: PropTypes.bool,
  hint: PropTypes.string,
  errors: PropTypes.arrayOf(PropTypes.string),
  headerRenderer: PropTypes.func,
  footerRenderer: PropTypes.func,
  isHoverMode: PropTypes.bool,
  showTimeZoneDescription: PropTypes.bool,
  /** it will render left icon of label field */
  labelLeftIcon: PropTypes.node,
  /** it will render right icon of label field */
  labelRightIcon: PropTypes.node
};

export const DatePickerInput = forwardRef(DatePickerInputComponent);
